/* eslint-disable eqeqeq */
import _, { get } from "lodash";
import { action, reaction, makeObservable } from "mobx";
import { inject, observer } from "mobx-react";
import React from "react";

import { getECGList } from "../../serviceCalls";
import Icon from "../../shared/Icon";
import Loader from "../../shared/Loader/Loader";
import Swal from "../../shared/Swal";
import Widget from "../Widget";
import { MODULE_ID } from "../../shared/moduleIds";
import "./ECG.scss";
import ECG12Lead from "./ECG12Lead";
import ECGStats from "./ECGStats";
import ECGStore from "./ECGStore";
import StatementEditor from "./StatementEditor";
import className from "../../shared/className";
import { formatDate, formatTime } from "../../shared/dateTimeUtils";

class ECG extends Widget {
  previousSelectedLead;
  state = {};
  constructor(props) {
    super(props, { moduleId: MODULE_ID.CARDIOLOGY });

    // todo: these methods should be by action wrapped instead.
    makeObservable(this, {
      setScrubberWidth: action,
      toolbarSelect: action,
      getECGList: action,
      selectECG: action,
      selectLead: action,
      toggleCompare: action,
    });

    this.store = new ECGStore();

    if (props.ecgList) {
      this.store.ECGList = props.ecgList;
      if (props.selectedECG) {
        this.store.selectedECG = props.selectedECG;
        this.selectECG(props.selectedECG);
      } else {
        this.store.selectedTool = "list";
      }
    } else {
      this.getECGList();
    }

    this.store.selectedLeadIndex =
      this.props.selectedLeadIndex >= 0
        ? this.props.selectedLeadIndex
        : get(this.props, "match.params.lead");
    this.selectLead = this.props.selectLead || this.selectLead.bind(this);
    this.selectECG = this.selectECG.bind(this);
    this.toggleCompare = props.toggleCompare || this.toggleCompare.bind(this);
    this.setScrubberWidth =
      props.setScrubberWidth || this.setScrubberWidth.bind(this);
    this.preSelectLead = this.preSelectLead.bind(this);
    this.observeRoute = _.debounce(this.observeRoute.bind(this)).bind(this);
    this.refresh = this.refresh.bind(this);
    this.toggleEditMode = this.toggleEditMode.bind(this);
    this.saveEdit = this.saveEdit.bind(this);
    this.cancelEdit = this.cancelEdit.bind(this);
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  observeRoute() {
    if (!_.get(this.store, "ECGList")) {
      return;
    }

    let ecgSelection = this.store.ECGList.find(
      (ecg) => ecg.ecgId === get(this.props, "match.params.ecgId"),
    );

    if (ecgSelection && ecgSelection !== this.store.selectedECG) {
      this.selectECG(ecgSelection);
    }
  }

  componentDidMount() {
    if (!this.props.history) return;
    const blockNavigation = (event) => {
      if (this.store.editMode) {
        event.preventDefault();
        return "Any information you edited will be undone. Are you sure you want to undo edits?";
      }
      return event;
    };
    window.addEventListener("beforeunload", blockNavigation);
    const unblockHistory = this.props.history.block(({ pathname }) => {
      if (this.store.editMode && !pathname.startsWith(this.lockedPath))
        return "Any information you edited will be undone. Are you sure you want to undo edits?";
    });
    this.unblockNavigation = () => {
      window.removeEventListener("beforeunload", blockNavigation);
      unblockHistory();
    };
    document.addEventListener("gesturestart", this.preventDefault);
  }
  componentWillUnmount() {
    this.unblockNavigation && this.unblockNavigation();
    document.removeEventListener("gesturestart", this.preventDefault);
  }

  componentDidUpdate() {
    reaction(
      () => this.store.chartsStore.quarterLeadWidth,
      () => this.store.chartsStore.restoreMarkers(),
    );
    reaction(
      () => this.store.editMode,
      (editMode) => (this.props.store.hideMainNav = editMode),
    );
  }

  UNSAFE_componentWillReceiveProps(props) {
    if (this.props.compare) {
      this.store.ECG = props.ecg;
    }
  }

  refresh() {
    if (!this.props.ecgList) {
      this.getECGList();
    }
  }

  render() {
    if (this.state.hasError) {
      return <h1>Something went wrong.</h1>;
    }
    return (
      <div
        className={this.cn({
          "ecg widget": 1,
          "animate-out": this.props.animateOut,
          "compare-mode": this.store.compareMode,
        })}
        style={{ height: this.props.height }}
      >
        {this.props.compare ? null : (
          <nav className="sub-nav">
            <a
              href="ecgs"
              className={this.cn({ active: !this.store.selectedLead })}
              onClick={(e) => this.selectLead(null, e)}
            >
              12 Lead
            </a>
            <a
              href="ecgs/lead"
              className={this.cn({
                active: this.store.selectedLead,
                disabled: !this.store.ECG,
              })}
              onClick={(e) =>
                this.selectLead(
                  this.store.previousSelectedLead ||
                    get(this.store, "ECG.leads[0]"),
                  e,
                )
              }
            >
              Single lead
            </a>

            <div className="edit-compare">
              {this.store.checkingEdit ? (
                <Loader size="button" />
              ) : (
                <a
                  href="edit"
                  className={this.cn({
                    edit: 1,
                    active: this.store.editMode,
                    disabled:
                      !_.get(this.store.ECG, "shouldShowEditConfirm") ||
                      !this.store.ECG ||
                      this.store.compareMode ||
                      this.store.loadingStatementLibrary,
                  })}
                  onClick={this.toggleEditMode}
                  data-testid="edit"
                >
                  Edit
                </a>
              )}
              <a
                href="ecgs/compare"
                className={this.cn({
                  "compare-link": 1,
                  active: this.store.compareMode,
                  disabled: !this.store.ECG,
                })}
                onClick={this.toggleCompare}
                data-testid="compare"
              >
                Compare
              </a>
            </div>
          </nav>
        )}
        {this.store.compareMode && this.store.testsRelated === false ? (
          <div className="non-compare-warning">
            *NOTE: The comparison statement displayed does not match the ECGs
            you have selected for comparison.
          </div>
        ) : null}
        {this.store.loading ? (
          <Loader />
        ) : (
          <div className="ecg-body">
            <div className="ecg-toolbar">
              <div
                {...this.className({
                  "list-option": 1,
                  selected: this.store.selectedTool === "list",
                  disabled: this.store.editMode,
                })}
                onClick={(e) => this.toolbarSelect("list", false, e)}
              >
                <Icon symbol="list" />
              </div>
              <div
                className={
                  "waveform-tool" +
                  (this.store.selectedTool === "waveform" ? " selected" : "")
                }
                onClick={(e) => this.toolbarSelect("waveform", false, e)}
              >
                <Icon symbol="waveform" />
              </div>
              <div
                className={
                  "info" +
                  (this.store.selectedTool === "info" ? " selected" : "")
                }
                onClick={() => this.toolbarSelect("info")}
              >
                <Icon symbol="info" />
              </div>
              {this.props.compare ? null : (
                <React.Fragment>
                  <div className="label">TOOLS</div>
                  <div
                    {...this.className({
                      calipers: true,
                      selected: this.store.caliperMode === "horizontal",
                      disabled: this.store.compareMode,
                    })}
                    onClick={(e) => this.toolbarSelect("horizontal", true, e)}
                  >
                    <Icon symbol="calipers" />
                  </div>
                  <div
                    {...this.className({
                      calipers: true,
                      selected: this.store.caliperMode === "vertical",
                      disabled:
                        this.store.compareMode || !this.store.singleLeadMode,
                    })}
                    onClick={(e) => this.toolbarSelect("vertical", true, e)}
                  >
                    <Icon symbol="calipers-vertical" />
                  </div>
                </React.Fragment>
              )}
            </div>
            <div
              className={this.cn({
                "ecg-list": true,
                "tool-drawer": true,
                open: this.store.selectedTool === "list",
              })}
              data-testid="ecg-list"
            >
              <div className="list-head">
                <div
                  className="close"
                  title="close"
                  onClick={() => this.toolbarSelect(null)}
                ></div>
              </div>
              <ECGList store={this.store} selectECG={this.selectECG} />
            </div>
            <div
              className={this.cn({
                "waveform-list": true,
                "tool-drawer": true,
                open: this.store.selectedTool === "waveform",
              })}
            >
              <div className="list-head">
                <div
                  className="close"
                  title="close"
                  onClick={() => this.toolbarSelect(null)}
                ></div>
              </div>
              {this.store.ECG ? (
                this.store.ECG.leads.map((waveform, i) => (
                  <div
                    className={this.cn({
                      "waveform-item": true,
                      selected: this.selectedLeadIndex == i,
                    })}
                    key={i}
                    onClick={() => this.selectLead(i)}
                  >
                    <div className="title">{waveform.title}</div>
                  </div>
                ))
              ) : (
                <div className="no-ecg">No ECG tests loaded.</div>
              )}
            </div>
            <div
              className={this.cn({
                "ecg-detail": true,
                "tool-drawer": true,
                open: this.store.selectedTool === "info",
              })}
            >
              <div className="list-head">
                <div
                  className="close"
                  title="close"
                  onClick={() => this.toolbarSelect(null)}
                ></div>
              </div>
              {this.store.ECG ? (
                <div className="detail-body">
                  <div className="date-time">
                    {this.date(this.store.ECG.ecgDetails.acquisitionDateTime)} •{" "}
                    {this.time(this.store.ECG.ecgDetails.acquisitionDateTime)}
                  </div>
                  <div className="diagnosis">
                    {this.store.ECG.ecgDetails.diagnosis}
                  </div>
                  <table className="patient-info">
                    <tbody>
                      <tr>
                        <td className="label">Sex</td>
                        <td className="value">
                          {this.store.ECG.ecgDetails.sex}
                        </td>
                      </tr>
                      <tr>
                        <td className="label">DOB</td>
                        <td className="value">
                          {this.store.ECG.ecgDetails.dob ||
                            get(this.props, "store.patient.dob")}
                        </td>
                      </tr>
                      <tr>
                        <td className="label">Age</td>
                        <td className="value">
                          {this.store.ECG.ecgDetails.age}
                        </td>
                      </tr>
                      <tr>
                        <td className="label">Race</td>
                        <td className="value">
                          {this.store.ECG.ecgDetails.race ||
                            get(this.props, "store.patient.race")}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <ECGStats ecg={this.store.ECG} />
                  <div className="analysis">
                    {this.store.ECG.ecgDetails.analysis}
                  </div>
                </div>
              ) : (
                <div className="no-ecg">No ECG test selected.</div>
              )}
            </div>
            {this.store.loadingECG ? (
              <Loader />
            ) : this.props.ecg || this.store.ECG ? (
              <ECG12Lead
                {...this.props}
                ecg={this.props.ecg || this.store.ECG}
                ecgStore={this.store}
                selectLead={this.selectLead}
                selectedLead={
                  this.props.compare
                    ? get(this.props, "ecg.leads", [])[
                        this.props.selectedLeadIndex
                      ]
                    : this.store.selectedLead
                }
                scrubberWidth={
                  this.props.scrubberWidth || this.store.scrubberWidth
                }
                setScrubberWidth={this.setScrubberWidth}
                caliperMode={this.store.caliperMode}
                selectedLeadIndex={this.selectedLeadIndex}
              />
            ) : null}
            {this.store.compareMode && !this.props.compare ? (
              <ECG
                ref={(ref) => (this.ECG2 = ref)}
                compare={true}
                selectedECG={this.store.selectedCompareECG}
                ecg={this.store.compareECG}
                ecgList={this.store.ECGList}
                toggleCompare={this.toggleCompare}
                animateOut={this.store.animateOutCompare}
                selectLead={this.selectLead}
                scrubberWidth={this.store.scrubberWidth}
                setScrubberWidth={this.setScrubberWidth}
                selectedLeadIndex={this.selectedLeadIndex}
                asmpid={this.asmpid}
                parent={this}
                parentStore={this.store}
                store={this.props.store}
              />
            ) : null}
            {this.store.editMode ? (
              <StatementEditor
                ecg={this.store.ECG}
                save={this.saveEdit}
                cancel={this.cancelEdit}
                statementLibrary={this.store.statementLibrary}
              />
            ) : null}
          </div>
        )}
      </div>
    );
  }

  setScrubberWidth(w) {
    this.store.scrubberWidth = w;
  }

  preventDefault(e) {
    e.preventDefault();
  }
  toolbarSelect(option, caliperTool, e) {
    if (e && e.currentTarget.classList.contains("disabled")) {
      return;
    }

    if (caliperTool) {
      this.store.caliperMode =
        this.store.caliperMode === option ? null : option;
    } else {
      this.store.selectedTool =
        this.store.selectedTool === option ? null : option;
    }
  }

  async getECGList() {
    this.store.loading = true;
    this.store.ECGList = await getECGList(this.asmpid).finally(() => {
      this.store.loading = false;
    });

    // This is a hack to enforce ID type from simulator (it's sending integers).
    if (this.store.ECGList) {
      this.store.ECGList.forEach(
        (item) => (item.ecgId = item.ecgId.toString()),
      );
    }

    // Preselect the ECG from the URL, or open the menu.
    if (get(this.store, "ECGList.length")) {
      let ecgSelection = this.store.ECGList.find(
        (ecg) => ecg.ecgId === get(this.props, "match.params.ecgId"),
      );
      if (ecgSelection) {
        this.selectECG(ecgSelection);
      } else if (this.store.ECGList.length) {
        this.selectECG(this.store.ECGList[0]);
      }

      this.store.selectedTool = "list";

      // Auto-collapse the menu.
      setTimeout(() => (this.store.selectedTool = undefined), 3000);
    }
  }

  async selectECG(ECGItem) {
    if (this.store.loadingECG || this.props.parentStore?.loadingECG) {
      return;
    }
    this.store.selectedECG = ECGItem;
    this.store.loadingECG = true;
    if (this.props.compare)
      return this.props.parent.compareECGs(
        this.props.parentStore.selectedECG,
        ECGItem,
      );
    if (this.store.compareMode && this.ECG2?.store.selectedECG)
      return this.compareECGs(ECGItem, this.ECG2?.store.selectedECG);
    this.historyPush(
      `/patient/${this.asmpid}/ecgs/${ECGItem.ecgId}${
        this.props.match.lead ? `/lead/${this.props.match.lead}` : ""
      }`,
    );
    await this.store.fetchECG({
      ecgId: ECGItem.ecgId,
      asmpid: this.asmpid,
    });
    this.preSelectLead();
  }

  compareECGs(ecgItem1, ecgItem2) {
    this.ECG2.store.loadingECG = true;
    const store = this.props.parentStore || this.store;
    store
      .fetchECGComparison({
        asmpid: this.asmpid,
        ecgId: ecgItem1.ecgId,
        ecg2id: ecgItem2.ecgId,
      })
      .then(() => {
        this.preSelectLead();
        this.ECG2.preSelectLead();
      })
      .finally(() => {
        this.ECG2.store.loadingECG = false;
      });
  }

  preSelectLead() {
    if (get(this.props, "match.lead") || this.props.selectedLeadIndex) {
      this.selectLead(
        this.props.compare
          ? this.props.selectedLeadIndex
          : +get(this.props, "match.lead"),
      );
    }
  }

  selectLead(lead, e) {
    e && e.preventDefault();
    if (this.store.loadingECG) return;
    if (!this.store.ECG) return;

    // Allow the index to be send or the object.
    if (typeof lead !== "object") {
      lead = this.store.ECG.leads[lead];
    }

    // deselect on second click.
    this.store.selectedLead =
      e && this.store.selectedLead === lead ? null : lead;

    // Store the index, because that is how it should be referenced.
    let selectedLeadIndex = this.store.ECG.leads.indexOf(
      this.store.selectedLead,
    );
    this.store.selectedLeadIndex =
      selectedLeadIndex === -1 ? undefined : selectedLeadIndex;

    // update the history for deep-linking.
    if (this.props.history) {
      this.historyPush(
        `/patient/${this.asmpid}/ecgs/${this.store.selectedECG.ecgId}${
          this.store.singleLeadMode
            ? `/lead/${this.store.selectedLeadIndex}`
            : ""
        }`,
      );
    }

    // Store the selection, so we can go back to it when clicking 'Single Lead' in subnav.
    if (this.store.selectedLead) {
      this.store.previousSelectedLead = this.store.selectedLead;
    }
  }

  // turns comparison mode on and off .
  toggleCompare(e) {
    e && e.preventDefault();

    if (!this.store.ECG) {
      return;
    }

    this.store.scrubberWidth =
      this.store.chartsStore.scrubberWidth / this.store.chartsStore.stripWidth;

    // don't use if still in transition or if there aren't any ECGs to compare.
    if (this.store.animateOutCompare) {
      return;
    }

    this.store.editMode = false;
    this.store.compareMode = !this.store.compareMode;
    this.store.selectedCompareECG = null;

    // provide delay for animation to finish.
    if (this.store.compareMode) {
      this.store.chartsStore.yOffset = 0;
    } else {
      this.store.animateOutCompare = true;
      this.selectECG(this.store.selectedECG);
      setTimeout(() => {
        this.store.selectedCompareECG = null;
        this.store.animateOutCompare = false;
        this.store.compareECG = null;
      }, 500);
    }
  }

  toggleEditMode(e) {
    e?.preventDefault();
    if (this.store.editMode) return;
    if (e?.target.classList.contains("disabled")) return;
    if (this.store.selectedTool === "list") this.store.selectedTool = "";
    this.store
      .startEditMode()
      .then((res) => {
        if (res.userMessage) {
          return Swal.fire({
            icon: res.allowEdit ? "success" : "error",
            text: res.userMessage,
            showCancelButton: res.allowEdit,
          }).then(
            (swalResult) =>
              (this.store.editMode = !!res.allowEdit && swalResult.value),
          );
        } else {
          return (this.store.editMode = res.allowEdit);
        }
      })
      .then((editMode) => {
        if (editMode) {
          const match = this.props.location.pathname.match(/.*\/ecgs\/[^/]+/);
          this.lockedPath = match && match[0];
        } else {
          this.lockedPath = null;
        }
      });
  }
  saveEdit(statements) {
    return Swal.fire({
      text: "Are you sure you want to confirm this ECG?",
      showCancelButton: true,
      confirmButtonText: "Confirm (Yes)",
      cancelButtonText: "Cancel (No)",
    }).then((result) => {
      return result.value
        ? this.store.saveStatements(statements).then((response) => {
            this.selectECG(this.store.selectedECG);
            if (response.userMessage) {
              Swal.fire({
                text: response.userMessage,
                icon: response.confirmSuccess ? "success" : "error",
              });
            }
            return response;
          })
        : Promise.reject("cancelled");
    });
  }
  cancelEdit() {
    Swal.fire({
      icon: "warning",
      text: "Any information you edited will be undone. Are you sure you want to undo edits?",
      showCancelButton: true,
      confirmButtonText: "Undo Edits",
      cancelButtonText: "Cancel",
    }).then((res) => {
      if (res.value) {
        this.store.editMode = false;
      }
    });
  }

  get ecgId() {
    return +this.props.match.params.ecgId;
  }

  get disableNextEcg() {
    return this.ecgId >= _.get(this.store.ECGList, "length");
  }

  get disablePrevEcg() {
    return this.ecgId === 1;
  }

  get selectedLeadIndex() {
    return this.props.compare
      ? this.props.selectedLeadIndex
      : this.store.selectedLeadIndex;
  }
}

export default inject("store")(observer(ECG));

function ECGList({ store, selectECG }) {
  return (
    <>
      {store.ECGList?.length ? (
        store.ECGList.map((ecgItem, i) => (
          <div
            {...className({
              "ecg-item": true,
              selected: store.selectedECG === ecgItem,
              unconfirmed: ecgItem.confirmed === false,
            })}
            key={i}
            onClick={() => {
              selectECG(ecgItem);
              store.selectedTool = null;
            }}
          >
            <div className="date-time">
              <div className="date">{formatDate(ecgItem.date)}</div>
              <div className="time">{formatTime(ecgItem.date)}</div>
            </div>
            <div className="diagnosis">{ecgItem.diagnosis}</div>
            <div className="analysis">{ecgItem.analysis}</div>
          </div>
        ))
      ) : (
        <div className="no-ecg">No ECG tests loaded.</div>
      )}
    </>
  );
}
